import { useStore } from 'vuex'
import { extractPhotoGpsData } from '@/js/utils/exif-gps-utils.js'
import { compressImageToTarget } from '@/js/utils/image-compress.js'
import { addDraw, markerAddAndRemove } from '@/js/downLoad'
import axios from 'axios'
import { appendIdToken } from '@/js/utils/firebaseAuth'
import { consumeToken } from '@/js/tokenService'

export function useGpsPhotoImport() {
  const store = useStore()

  /**
   * GPS写真インポートのメイン処理
   * @param {File[]} files - 画像ファイルの配列
   * @param {Function} showErrorDialog - エラーダイアログ表示用のコールバック
   */
  async function handleGpsPhotosImport(files, showErrorDialog = null) {
    // 0. HEICファイルをJPEGに変換（GPS情報を保持）
    const convertedFilesWithGps = await convertHeicFilesToJpeg(files)

    // 1. 進捗表示
    showMessage(`${convertedFilesWithGps.length}枚の写真を処理中...`, 'info')

    // 2. EXIF GPS抽出（並列）- 保持したGPS情報を優先使用
    const photoDataArray = await Promise.all(
      convertedFilesWithGps.map(async (item) => {
        // HEICから変換された場合、保持したGPS情報を使用
        if (item.preservedGps) {
          const exifData = await extractPhotoGpsData(item.file)
          return {
            ...exifData,
            gps: item.preservedGps, // 元のGPS情報を優先
          }
        }
        // 通常のファイルはそのまま抽出
        return extractPhotoGpsData(item.file)
      })
    )

    // 3. GPS情報がある写真のみフィルタ
    let validPhotos = photoDataArray.filter((data) => data.gps !== null)
    const invalidPhotos = photoDataArray.filter((data) => data.gps === null)
    const invalidCount = invalidPhotos.length

    // 4. GPS情報なし写真の処理（Geolocation APIフォールバック）
    if (invalidCount > 0) {
      // iOS判定: iOSの場合は特別なメッセージを表示
      const isiOS = isIOS()

      // iOSの場合は警告ダイアログを表示
      if (isiOS && showErrorDialog) {
        showErrorDialog({
          title: 'iOSで位置情報が取得できませんでした',
          message: `
            <div style="line-height: 1.8;">
              <p style="margin-bottom: 16px; font-size: 15px;">
                <strong>${invalidCount}枚の写真から位置情報を読み取れませんでした。</strong><br>
                現在地を使用して写真を配置します。
              </p>
              <div style="background: #e3f2fd; padding: 16px; border-radius: 8px; border-left: 4px solid #2196f3; margin-bottom: 16px;">
                <p style="margin: 0 0 12px 0; font-weight: 600; color: #1565c0;">
                  📱 次回から位置情報付きで読み込むには
                </p>
                <ol style="margin: 0; padding-left: 20px; color: #1565c0; font-size: 14px;">
                  <li style="margin-bottom: 8px;">写真選択画面で左下の<strong>「オプション」</strong>をタップ</li>
                  <li style="margin-bottom: 8px;"><strong>「位置情報」をオン</strong>にする</li>
                  <li>写真を選択</li>
                </ol>
              </div>
              <p style="margin: 0; font-size: 13px; color: #666;">
                ※ iOSはプライバシー保護のため、デフォルトで写真の位置情報を削除します。
              </p>
            </div>
          `,
        })
      }

      // GPSがない写真には現在地をフォールバックとして使用
      if ('geolocation' in navigator) {
        if (isiOS) {
          showMessage(`現在地を取得中...`, 'info')
        } else {
          showMessage(`${invalidCount}枚の写真に位置情報がありません。現在地を取得中...`, 'info')
        }
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0
            })
          })
          const fallbackGps = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            altitude: position.coords.altitude
          }
          showMessage(`${invalidCount}枚の写真に現在地を設定しました`, 'success')

          // GPS情報なし写真に現在地を設定
          invalidPhotos.forEach(photo => {
            photo.gps = fallbackGps
          })
          validPhotos = [...validPhotos, ...invalidPhotos]
        } catch {
          // Geolocation失敗時
          if (validPhotos.length > 0) {
            // GPS付き写真がある場合は、それだけで続行
            showMessage(
              `${invalidCount}枚の写真の位置情報を取得できませんでした。${validPhotos.length}枚を処理します。`,
              'warning'
            )
          } else {
            // 全ての写真にGPSがなく、現在地も取得できない場合はエラー
            if (showErrorDialog) {
              showErrorDialog({
                title: '位置情報が見つかりません',
                message: getNoGpsErrorMessage(),
              })
            } else {
              showMessage(
                `位置情報を持つ写真がありませんでした。カメラアプリで撮影した写真をライブラリから選択してください。`,
                'error'
              )
            }
            return
          }
        }
      } else {
        // Geolocation API非対応の場合
        if (validPhotos.length > 0) {
          // GPS付き写真がある場合は、それだけで続行
          showMessage(
            `${invalidCount}枚の写真に位置情報がありませんでした。${validPhotos.length}枚を処理します。`,
            'warning'
          )
        } else {
          // 全ての写真にGPSがなく、Geolocationも使えない場合はエラー
          if (showErrorDialog) {
            showErrorDialog({
              title: '位置情報が見つかりません',
              message: getNoGpsErrorMessage(),
            })
          } else {
            showMessage(
              `位置情報を持つ写真がありませんでした。カメラアプリで撮影した写真をライブラリから選択してください。`,
              'error'
            )
          }
          return
        }
      }
    }

    // 5. 画像アップロード（バッチ処理）
    const uploadedPhotos = await uploadPhotosInBatches(validPhotos, 3)

    // 6. ドローに追加（addDraw関数が自動的に地図をフィットする）
    addPhotosToDrawLayer(uploadedPhotos)

    // 7. 完了メッセージ
    showMessage(`${uploadedPhotos.length}枚の写真を地図に追加しました！`, 'success')
  }

  /**
   * HEICファイルをJPEGに変換（GPS情報を保持）
   * @param {File[]} files - ファイル配列
   * @returns {Promise<Array<{file: File, preservedGps: Object|null}>>} 変換後のファイルと保持したGPS情報
   */
  async function convertHeicFilesToJpeg(files) {
    const convertedFiles = []
    const heicFiles = files.filter((f) => {
      const ext = getFileExtension(f)
      return ext === 'heic' || ext === 'heif'
    })
    const totalHeic = heicFiles.length

    // HEIC変換の進捗を表示
    if (totalHeic > 0) {
      showMessage(`HEIC画像を変換中... (0/${totalHeic})`, 'info')
    }

    let convertedCount = 0

    for (const file of files) {
      const ext = getFileExtension(file)

      if (ext === 'heic' || ext === 'heif') {
        try {
          // 元のHEICファイルからGPS抽出（変換で失われるため保持）
          const originalGpsData = await extractPhotoGpsData(file)

          // サーバー側でHEIC→JPEG変換
          const formData = new FormData()
          formData.append('file', file)
          await appendIdToken(formData)

          const response = await axios.post('https://kenzkenz.net/myphp/heicToJpeg.php', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })

          if (response.data.error) {
            throw new Error(response.data.error)
          }

          if (!response.data.success || !response.data.fileName) {
            throw new Error('サーバーからの応答が不正です')
          }

          // サーバーに保存されたJPEGファイルを取得
          const jpegUrl = `https://kenzkenz.net/myphp/image/${response.data.fileName}`
          const jpegResponse = await fetch(jpegUrl)
          const jpegBlob = await jpegResponse.blob()

          // Fileオブジェクトに変換
          const newFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg')
          const jpegFile = new File([jpegBlob], newFileName, {
            type: 'image/jpeg',
            lastModified: file.lastModified,
          })

          // ファイルと保持したGPS情報を返す（元のGPSを優先）
          convertedFiles.push({
            file: jpegFile,
            preservedGps: originalGpsData.gps, // 元のGPS情報を保持
          })
          convertedCount++

          // 進捗更新
          if (totalHeic > 0) {
            showMessage(`HEIC画像を変換中... (${convertedCount}/${totalHeic})`, 'info')
          }
        } catch (error) {
          console.error('HEIC変換エラー:', error)
          showMessage(`${file.name}の変換に失敗しました`, 'error')
          // 変換失敗したファイルはスキップ
        }
      } else {
        // HEIC以外はそのまま追加（preservedGpsはnull）
        convertedFiles.push({
          file: file,
          preservedGps: null,
        })
      }
    }

    return convertedFiles
  }

  /**
   * ファイル拡張子を取得
   * @param {File} file - ファイル
   * @returns {string} 小文字の拡張子
   */
  function getFileExtension(file) {
    return file.name.split('.').pop().toLowerCase()
  }

  /**
   * バッチアップロード（3枚ずつ）
   * @param {Object[]} photoDataArray - 写真データの配列
   * @param {number} batchSize - バッチサイズ
   * @returns {Promise<Object[]>} アップロード結果の配列
   */
  async function uploadPhotosInBatches(photoDataArray, batchSize = 3) {
    const results = []
    const total = photoDataArray.length

    for (let i = 0; i < total; i += batchSize) {
      const batch = photoDataArray.slice(i, i + batchSize)

      // 進捗更新
      showMessage(`画像をアップロード中... ${Math.min(i + batchSize, total)}/${total}`, 'info')

      // バッチ内並列アップロード
      const batchResults = await Promise.all(batch.map((photoData) => uploadSinglePhoto(photoData)))

      results.push(...batchResults)
    }

    return results
  }

  /**
   * 単一写真アップロード
   * @param {Object} photoData - 写真データ
   * @returns {Promise<Object>} アップロード結果
   */
  async function uploadSinglePhoto(photoData) {
    try {
      // 1. 画像圧縮
      const compressedBlob = await compressImageToTarget(photoData.file, {
        targetBytes: 1 * 1024 * 1024, // 1MB
        maxLongSide: 2048,
        preferWebP: false,
      })

      // 2. FormData作成
      const formData = new FormData()
      formData.append('file', compressedBlob, photoData.originalName)
      formData.append('dir', store.state.userId)
      await appendIdToken(formData)

      // トークン消費（1枚につき2pt）- アップロード前にチェック
      const tokenResult = await consumeToken('draw_photo_upload')
      if (!tokenResult.success && tokenResult.error !== 'NETWORK_ERROR_IGNORED') {
        return {
          ...photoData,
          imageUrl: null,
          uploadSuccess: false,
        }
      }

      // 3. アップロード
      const response = await fetch('https://kenzkenz.net/myphp/upload_picture.php', {
        method: 'POST',
        body: formData,
      })

      const result = await response.json()

      return {
        ...photoData,
        imageUrl: result.webUrl,
        uploadSuccess: true,
      }
    } catch (error) {
      console.error(`アップロード失敗: ${photoData.originalName}`, error)
      return {
        ...photoData,
        imageUrl: null,
        uploadSuccess: false,
      }
    }
  }

  /**
   * ドローレイヤーに追加
   * @param {Object[]} uploadedPhotos - アップロード済み写真の配列
   */
  function addPhotosToDrawLayer(uploadedPhotos) {
    // 有効な写真のみフィルタ
    const validPhotos = uploadedPhotos.filter((photo) => photo.gps && photo.uploadSuccess)

    if (validPhotos.length === 0) {
      return
    }

    // GeoJSON FeatureCollectionを作成
    const features = validPhotos.map((photo) => {
      // MapLibre互換のプロパティのみを設定（配列やundefined値を避ける）
      const props = {
        label: photo.timestamp ? formatPhotoTimestamp(photo.timestamp) : photo.originalName,
        color: 'black',
        'point-color': 'red',
        'text-size': 20,
        labelType: '1', // 文字列型（吹き出しモード）
        pictureUrl: photo.imageUrl || '', // downLoad.jsで使用されるプロパティ名
        fileName: photo.originalName || '',
        photoImport: true,
        // 通常ラベル用のプロパティ（labelType='1'なので実際には使われない）
        offsetValue: [0.6, 0],
        textAnchor: 'left',
        textJustify: 'left',
      }

      // 存在する場合のみ追加（すべて文字列に変換）
      if (photo.timestamp) {
        props.timestamp = String(photo.timestamp)
      }
      if (photo.camera) {
        props.camera = String(photo.camera)
      }
      if (photo.gps.altitude != null) {
        props.altitude = String(photo.gps.altitude)
      }

      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [photo.gps.longitude, photo.gps.latitude],
        },
        properties: props,
      }
    })

    const geojson = {
      type: 'FeatureCollection',
      features: features,
    }

    // 既存のaddDraw関数を使用（isFit=true, isNini=false）
    addDraw(geojson, true, false)

    // マーカー（吹き出し画像）を生成
    markerAddAndRemove(true)
  }

  /**
   * 地図フィット
   * @param {Object[]} uploadedPhotos - アップロード済み写真の配列
   */
  function fitMapToPhotos(uploadedPhotos) {
    const validPhotos = uploadedPhotos.filter((p) => p.gps && p.uploadSuccess)
    if (validPhotos.length === 0) return

    const map = store.state.map01
    if (!map) return

    const lngs = validPhotos.map((p) => p.gps.longitude)
    const lats = validPhotos.map((p) => p.gps.latitude)

    const bounds = [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ]

    map.fitBounds(bounds, {
      padding: 120,
      maxZoom: 16,
      duration: 1000,
    })
  }

  /**
   * 撮影日時フォーマット
   * @param {Date|string} timestamp - 撮影日時
   * @returns {string} フォーマット済み日時文字列
   */
  function formatPhotoTimestamp(timestamp) {
    if (!timestamp) return ''
    const date = new Date(timestamp)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hour = String(date.getHours()).padStart(2, '0')
    const min = String(date.getMinutes()).padStart(2, '0')
    return `${year}/${month}/${day} ${hour}:${min}`
  }

  /**
   * スナックバー表示
   * @param {string} text - 表示テキスト
   * @param {string} color - 色 (使用されない、互換性のため残す)
   */
  function showMessage(text, color = 'info') {
    // App.vueのsnackbarを使用（window.vueAppはmounted()で設定済み）
    const app = window.vueApp
    if (app) {
      app.snackbarText = text
      app.snackbar = true
    } else {
      console.warn('window.vueApp not found, using console.log instead:', text)
    }
  }

  /**
   * iOS/iPadOS判定
   */
  function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  }

  /**
   * GPS取得失敗時のエラーメッセージを生成
   * iOS向けには「オプション」ボタンの説明を追加
   */
  function getNoGpsErrorMessage() {
    const iosInstructions = isIOS() ? `
      <div style="background: #e3f2fd; padding: 16px; border-radius: 8px; border-left: 4px solid #2196f3; margin-bottom: 16px;">
        <p style="margin: 0 0 12px 0; font-weight: 600; color: #1565c0;">
          📱 iPhone/iPadをお使いの場合（重要）
        </p>
        <p style="margin: 0; color: #1565c0; font-size: 14px;">
          写真を選択する画面で、左下の<strong>「オプション」</strong>をタップし、<strong>「位置情報」をオン</strong>にしてください。<br>
          iOSはプライバシー保護のため、デフォルトで位置情報を削除します。
        </p>
      </div>
    ` : ''

    return `
      <div style="line-height: 1.8;">
        <p style="margin-bottom: 16px; font-size: 15px;">
          <strong>選択された写真に位置情報が含まれていません。</strong>
        </p>
        ${iosInstructions}
        <div style="background: #fff3cd; padding: 16px; border-radius: 8px; border-left: 4px solid #ffc107; margin-bottom: 16px;">
          <p style="margin: 0 0 12px 0; font-weight: 600; color: #856404;">
            📍 位置情報付き写真を取得する方法
          </p>
          <ol style="margin: 0; padding-left: 20px; color: #856404;">
            <li style="margin-bottom: 8px;">カメラアプリで撮影した写真をライブラリから選択</li>
            <li style="margin-bottom: 8px;">設定 > プライバシー > 位置情報サービス > カメラ：オン</li>
            <li>iPhone/iPad: 写真選択時に「オプション」→「位置情報」をオン</li>
          </ol>
        </div>
        <p style="margin: 0; font-size: 14px; color: #666;">
          ⚠️ ブラウザから直接撮影した写真には位置情報が含まれません。<br>
          必ずカメラアプリで撮影した写真をライブラリから選択してください。
        </p>
      </div>
    `
  }

  return {
    handleGpsPhotosImport,
  }
}
